import { useRef, useState } from 'react';
import { Invoice, ESTADO_LABELS, ESTADO_COLORS } from './types';

interface Props {
  invoice: Invoice;
  tallerNombre: string;
  tallerNif: string;
  tallerDireccion: string;
  tallerEmail: string;
  tallerTelefono: string;
  onClose: () => void;
  onEdit: () => void;
}

const InvoicePreview = ({ invoice, tallerNombre, tallerNif, tallerDireccion, tallerEmail, tallerTelefono, onClose, onEdit }: Props) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = pdfHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();

      while (heightLeft > 0) {
        position -= pdf.internal.pageSize.getHeight();
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }

      pdf.save(`${invoice.numero}.pdf`);
    } catch (err) {
      console.error('Error generando PDF:', err);
    } finally {
      setDownloading(false);
    }
  };

  const fmt = (n: number) => n.toFixed(2).replace('.', ',') + ' €';
  const fmtDate = (d: string) => {
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col">
      {/* Toolbar */}
      <div className="flex-shrink-0 bg-zinc-900 border-b border-zinc-800 px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white cursor-pointer transition-colors">
            <i className="ri-arrow-left-line text-lg" />
          </button>
          <div>
            <p className="text-white font-bold text-sm">{invoice.numero}</p>
            <p className="text-zinc-500 text-xs">{invoice.cliente_nombre}</p>
          </div>
          <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${ESTADO_COLORS[invoice.estado]}`}>
            {ESTADO_LABELS[invoice.estado]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="flex items-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-600 text-xs font-semibold rounded-lg cursor-pointer transition-all whitespace-nowrap"
          >
            <i className="ri-edit-line" /> Editar
          </button>
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="flex items-center gap-2 px-5 py-2 bg-yellow-400 text-black text-xs font-black rounded-lg hover:bg-yellow-300 disabled:opacity-60 cursor-pointer transition-all whitespace-nowrap"
          >
            {downloading ? (
              <><i className="ri-loader-4-line animate-spin" /> Generando...</>
            ) : (
              <><i className="ri-download-2-line" /> Descargar PDF</>
            )}
          </button>
        </div>
      </div>

      {/* Scrollable preview */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8 flex justify-center">
        {/* A4 paper */}
        <div
          ref={printRef}
          className="bg-white w-full max-w-[794px] min-h-[1123px] p-12 shadow-2xl"
          style={{ fontFamily: "'Inter', sans-serif", color: '#1a1a1a' }}
        >
          {/* Header */}
          <div className="flex justify-between items-start mb-10">
            {/* Taller info */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-yellow-400 flex items-center justify-center rounded-lg flex-shrink-0">
                  <i className="ri-tools-line text-black text-lg" />
                </div>
                <div>
                  <h1 className="text-xl font-black text-gray-900 leading-tight">{tallerNombre}</h1>
                  {tallerNif && <p className="text-gray-500 text-xs">NIF: {tallerNif}</p>}
                </div>
              </div>
              <div className="text-gray-500 text-xs space-y-0.5 ml-1">
                {tallerDireccion && <p>{tallerDireccion}</p>}
                {tallerEmail && <p>{tallerEmail}</p>}
                {tallerTelefono && <p>{tallerTelefono}</p>}
              </div>
            </div>

            {/* Factura badge */}
            <div className="text-right">
              <div className="inline-block bg-gray-900 text-white px-5 py-2 rounded-xl mb-2">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">Factura</p>
                <p className="text-lg font-black">{invoice.numero}</p>
              </div>
              <div className="text-xs text-gray-500 space-y-0.5">
                <p><span className="font-semibold text-gray-700">Fecha:</span> {fmtDate(invoice.fecha)}</p>
                <p><span className="font-semibold text-gray-700">Vencimiento:</span> {fmtDate(invoice.fecha_vencimiento)}</p>
                <div className="mt-1">
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                    invoice.estado === 'pagada' ? 'bg-green-100 text-green-700' :
                    invoice.estado === 'emitida' ? 'bg-yellow-100 text-yellow-700' :
                    invoice.estado === 'cancelada' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {ESTADO_LABELS[invoice.estado]}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-200 mb-8" />

          {/* Cliente */}
          <div className="mb-8">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Facturar a</p>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="font-black text-gray-900 text-base">{invoice.cliente_nombre}</p>
              {invoice.cliente_nif && <p className="text-gray-500 text-xs mt-0.5">NIF/CIF: {invoice.cliente_nif}</p>}
              {invoice.cliente_direccion && <p className="text-gray-500 text-xs mt-0.5">{invoice.cliente_direccion}</p>}
              <div className="flex gap-4 mt-1">
                {invoice.cliente_email && <p className="text-gray-500 text-xs">{invoice.cliente_email}</p>}
                {invoice.cliente_telefono && <p className="text-gray-500 text-xs">{invoice.cliente_telefono}</p>}
              </div>
            </div>
          </div>

          {/* Tabla de líneas */}
          <div className="mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider rounded-tl-lg">Descripción</th>
                  <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider">Cant.</th>
                  <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider">Precio unit.</th>
                  <th className="text-center px-3 py-3 text-xs font-bold uppercase tracking-wider">IVA</th>
                  <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider rounded-tr-lg">Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lineas.map((l, i) => {
                  const lineTotal = l.cantidad * l.precio_unitario * (1 + l.iva / 100);
                  return (
                    <tr key={l.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 text-gray-800 font-medium">{l.descripcion || '—'}</td>
                      <td className="px-3 py-3 text-center text-gray-600">{l.cantidad}</td>
                      <td className="px-3 py-3 text-right text-gray-600">{fmt(l.precio_unitario)}</td>
                      <td className="px-3 py-3 text-center text-gray-600">{l.iva}%</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">{fmt(lineTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totales */}
          <div className="flex justify-end mb-8">
            <div className="w-72 space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal (sin IVA)</span>
                <span className="font-semibold">{fmt(invoice.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>IVA</span>
                <span className="font-semibold">{fmt(invoice.total_iva)}</span>
              </div>
              <div className="h-px bg-gray-300" />
              <div className="flex justify-between text-base font-black text-gray-900">
                <span>TOTAL</span>
                <span className="text-yellow-600 text-lg">{fmt(invoice.total)}</span>
              </div>
            </div>
          </div>

          {/* Notas */}
          {invoice.notas && (
            <div className="border-t border-gray-200 pt-6">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Notas y condiciones</p>
              <p className="text-gray-600 text-xs leading-relaxed">{invoice.notas}</p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-12 pt-6 border-t border-gray-100 flex justify-between items-center">
            <p className="text-gray-400 text-[10px]">Documento generado por {tallerNombre}</p>
            <p className="text-gray-400 text-[10px]">{invoice.numero} · {fmtDate(invoice.fecha)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;
